import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from './cn';

/**
 * DESIGN.md › button-*: at least 48px tall, 8px radius, label-md, one primary per screen. The
 * height is a minimum: with Large text a label can wrap, and the button grows around it.
 */
export const buttonVariants = cva(
  'inline-flex min-h-tap-target min-w-tap-target cursor-pointer items-center justify-center gap-internal-md rounded-sm px-content-sm py-internal-sm text-center font-brand text-label-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-highlight disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        primary:
          'bg-action-primary text-on-action-primary hover:bg-action-primary-focus active:bg-action-primary-active disabled:bg-action-primary-disabled',
        secondary:
          'border border-border-highlight bg-action-secondary text-on-action-secondary hover:bg-action-secondary-focus active:bg-action-secondary-active',
        'secondary-tint':
          'border border-action-primary-focus bg-action-secondary-focus text-on-action-secondary-focus hover:bg-action-secondary-active active:bg-action-secondary-active',
        // DESIGN.md › button-tertiary: white with the teal label and no border.
        tertiary:
          'bg-action-secondary text-on-action-secondary hover:bg-action-secondary-focus active:bg-action-secondary-active',
        onearly: 'bg-action-onearly text-on-action-onearly hover:opacity-90',
        social: 'bg-action-social text-on-surface hover:bg-surface-tertiary',
        ghost: 'bg-transparent text-on-action-secondary hover:bg-action-secondary-focus',
        // DESIGN.md › button-destructive: red fill, white label — the one action that can't be undone.
        destructive:
          'bg-action-destructive text-on-action-primary hover:opacity-90 active:opacity-80 disabled:bg-action-primary-disabled disabled:opacity-100',
        'destructive-ghost':
          'bg-transparent text-action-destructive hover:bg-surface-tertiary disabled:opacity-80',
      },
      width: { auto: '', full: 'w-full' },
      // `sm` is the 40px OnEarly social button (DESIGN.md › button-social); everything else is 48px.
      size: { md: '', sm: 'min-h-10 text-label-sm' },
      iconOnly: {
        false: '',
        true: 'relative shrink-0 rounded-full p-0 before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-tap-target before:w-full before:min-w-tap-target before:-translate-x-1/2 before:-translate-y-1/2',
      },
    },
    compoundVariants: [
      { iconOnly: true, size: 'sm', class: 'size-10 min-w-10' },
      { iconOnly: true, size: 'md', class: 'size-tap-target' },
    ],
    defaultVariants: { variant: 'primary', width: 'auto', size: 'md', iconOnly: false },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * The action is under way: a spinner leads the label and the button stops taking clicks,
   * so a tap on Continue or Verify is never met with a button that looks untouched while
   * the request runs.
   */
  loading?: boolean;
}

export function Button({
  className,
  variant,
  width,
  size,
  iconOnly,
  loading = false,
  disabled,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(buttonVariants({ variant, width, size, iconOnly }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <span
          aria-hidden
          className="size-5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : null}
      {iconOnly && loading ? null : children}
    </button>
  );
}
